import NoResultsScreen from './NoResultsScreen';
import { DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { ItemType } from 'antd/es/menu/interface';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { WorkspaceAccessResponse } from '../../api/workspace';
import { useGetUserFromAccessToken } from '../../common/auth';
import colorTokens from '../../constants/design_system/colors';
import { ScrollRestorationProvider, useScrollRestorationContext } from '../../context/ScrollRestorationContext';
import { useSprintHook } from '../../hooks/useSprintHook';
import { useTaskFiltering } from '../../hooks/useTaskFiltering';
import { useTaskFilterManagement } from '../../hooks/useTaskFilterManagement';
import { useTaskSelection } from '../../hooks/useTaskSelection';
import { useViewContext } from '../../pages/Space/Context/ViewContext';
import { queryClient } from '../../queryClient';
import { updateSpaceEpicsOrder, updateSpaceTasksOrder } from '../../serverState/helpers/tasksOrderHelper';
import { usePlanningSession } from '../../serverState/planning';
import { getCurrentWorkspaceActiveSprint } from '../../serverState/sprint';
import { useGetWorkspace } from '../../serverState/workspace';
import { useFilterStore } from '../../stores/filterStore';
import { Epic } from '../../types/Epic';
import { TaskStatus, defaultTaskStatuses } from '../../types/TaskStatus';
import Workspace, { WorkspaceFramework } from '../../types/Workspace';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../../types/WorkspaceView';
import { User } from '../../types/User';
import { Column } from '../../types/WorkspaceViewColumn';
import { WorkspaceViewSortDirection } from '../../types/WorkspaceViewSortBy';
import '../Backlog/Backlog.scss';
import TaskGroupSection from '../Backlog/TaskGroupSection/TaskGroupSection';
import { DeleteTasksModal } from '../Modal/DeleteTasksModal';
import { MoveSprintTaskToPlanningModal } from '../Modal/MoveSprintTaskToPlanningModal';
import { EndSprintModal } from '../Modal/EndSprintModal';
import TaskFiltersModal from '../Modal/TaskFiltersModal';
import { PlanningSessionHeader } from '../Planning/PlanningSessionHeader';
import { TaskTableDataType, TaskTableProps } from '../TaskTable/types/TaskTableTypes';
import { NoSprintInProgress } from '../UI/NoSprintInProgress';
import { TasksZeroState } from '../UI/TasksZeroState';
import ActionPanel from './ActionPanel/ActionPanel';
import { clearOwnerFilter, updateOwnerFilter } from './helpers/filterHelpers';
import { sortGroupTasks, sortStatusOptions } from './helpers/sortHelpers';
import { FiltersHeader } from './UI/FiltersHeader';
import { SprintHeader } from './UI/SprintHeader';
import { useGetTasks, useUpdateTaskMutation } from '../../serverState/tasks';
import { VirtualizedTaskList } from './VirtualizedTaskList/VirtualizedTaskList';
import { MenuProps } from 'antd';
import { taskRowMenuItems, TASK_ROW_MENU_KEYS } from '../UI/Utils/uiUtils';
import Task from '../../types/Task';
import { reconcileStageChange } from '../../serverState/helpers/taskStageCache';
import { isExpandColumnVisible } from './VirtualizedTaskList/columnVisibility';

/** Update a task (or nested subtask) in the dataSource array by id. */
const updateTaskInDataSource = (
  dataSource: TaskTableDataType[],
  taskId: number,
  updates: Partial<TaskTableDataType>
): TaskTableDataType[] =>
  dataSource.map((t) => {
    if (t.id === taskId) {
      return { ...t, ...updates };
    }
    if (t.subtasks?.some((s) => s.id === taskId)) {
      return {
        ...t,
        subtasks: t.subtasks.map((s) => (s.id === taskId ? { ...s, ...updates } : s)),
      };
    }
    return t;
  });

type TaskManagementProps = {
  actualSpaceId: number;
  spaceId: string | undefined;
  tableDataLoading: boolean;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  dataSource: TaskTableDataType[];
  setDataSource: (value: React.SetStateAction<TaskTableDataType[]>) => void;
  epics: Epic[];
  epicIds: number[];
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: any) => void;
  activeEpic: Epic | null;
  sensors: any;
  taskMenuProps: {
    items: ItemType[];
    onClick: any;
  };
  planning?: TaskTableProps;
  activeView: WorkspaceView;
  handleEditEstimationSettings: () => void;
  onImportClick?: () => void;
};

const TaskManagement = (props: TaskManagementProps) => {
  return (
    <ScrollRestorationProvider viewId={props.activeView.id}>
      <TaskManagementContent {...props} />
    </ScrollRestorationProvider>
  );
};

const TaskManagementContent = (props: TaskManagementProps) => {
  const {
    actualSpaceId,
    spaceId,
    tableDataLoading,
    usersWithAccessToSpace,
    dataSource,
    setDataSource,
    epics,
    activeEpic,
    taskMenuProps,
    planning,
    activeView,
    handleEditEstimationSettings,
  } = props;

  const workspaceQuery = useGetWorkspace();
  const tasksQuery = useGetTasks(Number(actualSpaceId));
  const currentUserQuery = useGetUserFromAccessToken();
  const viewContext = useViewContext();
  const activeSprintQuery = getCurrentWorkspaceActiveSprint();
  const activeSprintId = activeSprintQuery.data?.sprint.id;
  const planningSessionId = usePlanningSession().data?.session.id;
  const framework = workspaceQuery.data?.framework;
  const updateTaskMutation = useUpdateTaskMutation();
  // Use scroll restoration context
  const scrollRestorationContext = useScrollRestorationContext();

  // Row menu items for VirtualizedTaskList
  const rowMenuItems = useMemo(
    () => taskRowMenuItems(framework, activeView),
    [framework, activeView]
  );

  const [endSprintModalVisible, toggleEndSprintModal] = useState(false);

  // Search text lives in the filter store keyed by (workspace, view) so it
  // survives navigating into a task and back to this view (#2276). Each view
  // keeps its own search, so no imperative clear is needed when switching views.
  const searchValue = useFilterStore((s) => s.getSearch(activeView.workspaceId, activeView.id));
  const setSearch = useFilterStore((s) => s.setSearch);
  const setSearchValue = useCallback(
    (value: string) => setSearch(activeView.workspaceId, activeView.id, value),
    [setSearch, activeView.workspaceId, activeView.id]
  );

  // Filters
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);

  const {
    filters,
    setFilters,
    selectedFilters,
    isViewSelectedFilters,
    epicFilter,
    ownerFilter,
    statusFilter,
    deadlineFilter,
    priorityFilter,
    estimateFilter,
    handleSaveForAll,
    handleResetToSaved,
  } = useTaskFilterManagement(activeView, usersWithAccessToSpace, () => {
    messageApi.success('View filters saved');
    // Confirming "save for everyone" should dismiss the filters panel. #2402
    setIsFiltersModalOpen(false);
  });

  // Task Selection
  const {
    selectedItems,
    setSelectedItems,
    openDeleteModal,
    confirmLoadingDeleteTasks,
    deleteTaskIds,
    messageApi,
    contextHolder,
    handleDeleteSelected,
    handleDeleteSingleTask,
    handleConfirmDeleteTasks,
    handleCancelDelete,
    clearSelection,
    onBatchEdit,
  } = useTaskSelection({
    spaceId,
    // Just show the toast. `useTaskSelection` already clears the selection
    // synchronously for hidePanel actions; awaiting the toast here meant a second
    // clear fired ~3s later, when it auto-dismissed — long enough to wipe a
    // selection the user had made in the meantime and close the batch panel
    // under them. #2635
    onSuccess: (message) => {
      void messageApi.success(message);
    },
    onInfo: async (info) => {
      await messageApi.info(info);
    },
    onError: async (error) => {
      await messageApi.error(error);
    },
  });

  // State for sprint-to-planning confirmation modal
  const [showSprintToPlanningModal, setShowSprintToPlanningModal] = useState(false);
  const [rowActionTask, setRowActionTask] = useState<Task | null>(null);

  const moveTaskToPlanning = useCallback(
    (task: Task) => {
      // Must also flip `stage` to 'planning' — otherwise the task keeps stage:'sprint'
      // and stays in the Sprint view even though sprintId is cleared (#2563).
      const updates: Partial<Task> = {
        stage: 'planning',
        planningSessionId: planningSessionId ? Number(planningSessionId) : null,
        sprintId: null,
      };
      updateTaskMutation.mutate({
        taskId: task.id,
        task: { ...task, ...updates },
      });
      setDataSource((prev) => updateTaskInDataSource(prev, task.id, updates));
      queryClient.setQueryData(['tasks', Number(actualSpaceId)], (old: Task[] | undefined) =>
        old ? updateTaskInDataSource(old as any, task.id, updates) : old
      );
      void messageApi.success('Task added to planning!');
    },
    [planningSessionId, updateTaskMutation, messageApi, setDataSource, actualSpaceId]
  );

  const handleSprintToPlanningConfirm = useCallback(
    (task: Task) => {
      setRowActionTask(task);
      setShowSprintToPlanningModal(true);
    },
    []
  );

  const applyStageChange = useCallback(
    (item: Task, nextStage: Task['stage'], successMessage: string) => {
      const prevStage = item.stage;
      const fkUpdates: Partial<Task> =
        nextStage === 'sprint'
          ? { sprintId: activeSprintId ? Number(activeSprintId) : item.sprintId, planningSessionId: null }
          : nextStage === 'planning'
            ? { sprintId: null }
            : { sprintId: null, planningSessionId: null };
      const updates: Partial<Task> = { stage: nextStage, ...fkUpdates };
      updateTaskMutation.mutate({
        taskId: item.id,
        task: { ...item, ...updates },
      });
      if (nextStage === 'archive' || prevStage === 'archive' || prevStage === 'triage') {
        setDataSource((prev) => prev.filter((t) => t.id !== item.id));
      } else {
        setDataSource((prev) => updateTaskInDataSource(prev, item.id, updates));
      }
      reconcileStageChange(Number(actualSpaceId), item.id, prevStage, { stage: nextStage });
      void messageApi.success(successMessage);
    },
    [updateTaskMutation, setDataSource, actualSpaceId, messageApi, activeSprintId]
  );

  // Row menu handler for VirtualizedTaskList
  const onRowMenuClicked = useCallback(
    async (e: { key: React.Key }, item: Task) => {
      if (e.key === TASK_ROW_MENU_KEYS.deleteForever) {
        handleDeleteSingleTask(item.id);
      } else if (e.key === TASK_ROW_MENU_KEYS.archive) {
        applyStageChange(item, 'archive', 'Task archived');
      } else if (e.key === TASK_ROW_MENU_KEYS.unarchive) {
        applyStageChange(item, 'backlog', 'Task restored to Backlog');
      } else if (e.key === TASK_ROW_MENU_KEYS.moveToBacklog) {
        applyStageChange(item, 'backlog', 'Task moved to Backlog');
      } else if (e.key === TASK_ROW_MENU_KEYS.moveToSprint) {
        if (!workspaceQuery.data?.activeSprintId) {
          void messageApi.info('Sprint has not started yet!');
          return;
        }
        applyStageChange(
          item,
          'sprint',
          `Task added to ${framework === WorkspaceFramework.scrum ? 'sprint' : 'kanban'}!`
        );
      } else if (e.key === TASK_ROW_MENU_KEYS.addToPlanning || e.key === TASK_ROW_MENU_KEYS.moveToPlanning) {
        if (planning) {
          planning.customRowSelection?.addTaskToSelectedKeys(item);
        } else if (item.sprintId && activeSprintId && item.sprintId === activeSprintId) {
          setRowActionTask(item);
          setShowSprintToPlanningModal(true);
        } else {
          moveTaskToPlanning(item);
        }
      } else if (e.key === TASK_ROW_MENU_KEYS.removeFromSprint) {
        applyStageChange(
          item,
          'backlog',
          `Task removed from ${framework === WorkspaceFramework.scrum ? 'Sprint' : 'Kanban'}!`
        );
      } else if (e.key === TASK_ROW_MENU_KEYS.rename) {
        setTimeout(() => {
          flushSync(() => {
            (document.querySelector(`[task-edit-id='${item.id}']`) as HTMLElement)?.click();
          });
        }, 50);
      }
    },
    [workspaceQuery.data, framework, planning, activeSprintId, moveTaskToPlanning, applyStageChange, messageApi, handleDeleteSingleTask]
  );

  const virtualizedTaskMenuProps = useMemo(
    () => ({
      items: rowMenuItems,
      onClick: onRowMenuClicked,
    }),
    [rowMenuItems, onRowMenuClicked]
  );

  // Task Filtering
  const { unassignedTasks, activeTasks, tasksPerGroup } = useTaskFiltering({
    dataSource,
    selectedFilters,
    searchValue,
    groupBy: activeView.groupBy ?? Column.epic,
    currentUserId: currentUserQuery.data?.user.id,
    activeSprintId,
    epicFilter,
    ownerFilter,
    statusFilter,
    deadlineFilter,
    priorityFilter,
    estimateFilter,
    epics,
    usersWithAccessToSpace,
    workspaceQuery,
    framework,
    // Subitems off in Display (or not applicable to this view): no dimmed context parents. #2324
    hideSubitems: !isExpandColumnVisible({
      hiddenColumns: activeView.columns?.hidden ?? [],
      viewEntityType: activeView.entityType,
      viewGroup: activeView.group,
    }),
  });


  const usersMap = useMemo(() => {
    const map = new Map<number, User>();
    usersWithAccessToSpace.forEach((access) => {
      map.set(access.userId, access.User);
    });
    return map;
  }, [usersWithAccessToSpace]);

  // The Epics module renders a flat, ungrouped list straight off `activeTasks`. Its manual
  // order lives in the epics cache (orderIndex), not the space's TaskOrder, so the order map
  // is simply the current array position. Shared by the render and by handleSwitchToManualSort
  // so both agree on which epic sits where. (#2544)
  const isEpicView = activeView.entityType === WorkspaceViewEntityType.epic;
  const sortedEpics = useMemo(() => {
    if (!isEpicView) return [];
    return sortGroupTasks(
      activeTasks,
      new Map(activeTasks.map((task, index) => [task.id, index])),
      activeView.sortBy?.column,
      activeView.sortBy?.direction,
      undefined,
      undefined,
      workspaceQuery.data?.estimationType,
      undefined,
      usersMap
    );
  }, [
    isEpicView,
    activeTasks,
    activeView.sortBy?.column,
    activeView.sortBy?.direction,
    workspaceQuery.data?.estimationType,
    usersMap,
  ]);

  useEffect(() => {
    clearSelection();
  }, [spaceId]);

  const showNotAssignedGroup = () => {
    const selectedFiltersTypes = selectedFilters.map((filter) => filter.type);
    switch (activeView.groupBy) {
      case Column.epic:
        return epicFilter && selectedFiltersTypes.includes('epic')
          ? epicFilter.options.find((option) => option.selected && option.name === 'No Epic Assigned')
          : true;
      case Column.assignee:
        return ownerFilter && selectedFiltersTypes.includes('owner')
          ? ownerFilter.options.find((option) => option.selected && !option.metadata)
          : true;
      case Column.deadline:
        return deadlineFilter && selectedFiltersTypes.includes('deadline')
          ? deadlineFilter.options.find((option) => option.selected && option.name === 'No deadline')
          : true;
      case Column.estimate:
        return estimateFilter && selectedFiltersTypes.includes('estimate')
          ? estimateFilter.options.find((option) => option.selected && !option.metadata)
          : true;
      case Column.status:
        return false;
      case Column.priority:
        return false;
      case Column.taskType:
        return false;
      default:
        return true;
    }
  };

  const handleSwitchToManualSort = async (activeTaskId: number, overTaskId: number) => {
    // Epics reorder against their own visual order and their own endpoint. (#2544)
    if (isEpicView) {
      if (!workspaceQuery.data?.id) return;
      const epicOrder = sortedEpics.map((epic) => epic.id);
      const activeIndex = epicOrder.indexOf(activeTaskId);
      const overIndex = epicOrder.indexOf(overTaskId);
      if (activeIndex === -1 || overIndex === -1) return;

      await updateSpaceEpicsOrder(workspaceQuery.data.id, arrayMove(epicOrder, activeIndex, overIndex));
      viewContext?.setView({
        ...activeView,
        sortBy: { column: 'Manual', direction: WorkspaceViewSortDirection.desc },
      });
      return;
    }

    const taskOrder = workspaceQuery.data?.TaskOrder[0];
    const tasksOrderMap = new Map<number, number>(taskOrder?.taskIds?.map((id: number, index: number) => [id, index]));

    const sortByColumn = viewContext?.view?.sortBy?.column;
    const sortDirection = viewContext?.view?.sortBy?.direction;
    const statusOrder = sortStatusOptions(workspaceQuery.data?.TaskStatus || defaultTaskStatuses).map(
      (status) => status.id
    );

    // Helper function to flatten tasks including subtasks.
    // #2309: skip borrowed/context rows — they share a home row's id, and this order is
    // persisted to the backend, so duplicates would corrupt the saved task order.
    const flattenTasksWithSubtasks = (tasks: TaskTableDataType[]): number[] => {
      return tasks
        .filter((task) => !task.isGroupContext)
        .flatMap((task) => [
          task.id,
          ...(task.subtasks?.filter((subtask) => !subtask.isGroupContext).map((subtask) => subtask.id) || []),
        ]);
    };

    const tasksOrder = Array.from(
      new Set([
        ...flattenTasksWithSubtasks(
        sortGroupTasks(
          unassignedTasks,
          tasksOrderMap,
          sortByColumn,
          sortDirection,
          activeSprintId,
          planningSessionId,
          workspaceQuery.data?.estimationType,
          statusOrder,
          usersMap
        )
      ),
      ...tasksPerGroup
        .map((group) =>
          flattenTasksWithSubtasks(
            sortGroupTasks(
              group.tasks,
              tasksOrderMap,
              sortByColumn,
              sortDirection,
              activeSprintId,
              planningSessionId,
              workspaceQuery.data?.estimationType,
              statusOrder,
              usersMap
            )
          )
        )
        .flatMap((taskIds) => taskIds),
      ])
    );
    const newTasksOrder = arrayMove(tasksOrder, tasksOrder.indexOf(activeTaskId), tasksOrder.indexOf(overTaskId));

    if (workspaceQuery.data?.id) {
      await updateSpaceTasksOrder(workspaceQuery.data.id, newTasksOrder);

      viewContext?.setView({
        ...activeView,
        sortBy: { column: 'Manual', direction: WorkspaceViewSortDirection.desc },
      });
    }
  };

  const { unfinishedTasks, handleEndSprint } = useSprintHook(
    (message: string) => messageApi.success(message),
    (message: string) => messageApi.error(message)
  );

  const numberOfTask =
    unassignedTasks.filter((t) => !t.isGroupContext).length +
    tasksPerGroup.reduce((sum, curr) => sum + curr.tasks.filter((t) => !t.isGroupContext).length, 0);
  const hasActiveSearchOrFilters = selectedFilters.length > 0 || searchValue.length > 0;
  const noResults = hasActiveSearchOrFilters && numberOfTask === 0;
  // Epic view renders the (filtered) epics directly from `activeTasks`.
  const epicNoResults = hasActiveSearchOrFilters && activeTasks.length === 0;

  const noResultsScreen = <NoResultsScreen />;

  if (
    !activeSprintQuery.data &&
    workspaceQuery.data?.framework === WorkspaceFramework.scrum &&
    props.activeView.group === WorkspaceViewGroup.sprint
  ) {
    return <NoSprintInProgress />;
  }

  const isSprint = framework === WorkspaceFramework.scrum && props.activeView.group === WorkspaceViewGroup.sprint;

  // Check if we should show zero state (no tasks in the current view)
  const showZeroState = !tableDataLoading && dataSource.length === 0;
  const showTasks = !tableDataLoading && dataSource.length > 0;

  return (
    // Toolbar and (on sprint) the banner size themselves; the list wrapper below takes
    // whatever is left, so its height follows the chrome instead of assuming it. #2357
    <div className="flex min-h-0 flex-1 flex-col">
      {contextHolder}
      <div className="header-bar justify-between">
        <FiltersHeader
          activeView={activeView}
          menuProps={taskMenuProps}
          planningSessionId={planning?.planningSession ? Number(planning?.planningSession) : null}
          ownerFilter={ownerFilter}
          onSaveOwnerFilter={(ownerFilter) => setFilters(updateOwnerFilter(filters, ownerFilter) || filters)}
          onClearOwnerFilter={() => setFilters(clearOwnerFilter(filters))}
          isViewSelectedFilters={isViewSelectedFilters}
          selectedFiltersLength={selectedFilters.length}
          onFiltersClick={() => setIsFiltersModalOpen(!isFiltersModalOpen)}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          showMessage={(message) => messageApi.success(message)}
          resetToSavedFilters={handleResetToSaved}
          saveFiltersForAll={() => handleSaveForAll(filters)}
        />
      </div>
      {planning && (
        <PlanningSessionHeader
          planningSessionId={planning.planningSession}
          nextSprintName={planning.nextSprintName}
          dataSource={planning.dataSource}
        />
      )}
      {isSprint && (
        <div
          className="header-bar flex justify-between items-center"
          style={{ backgroundColor: colorTokens.yellow[100], height: '52px' }}
        >
          <SprintHeader
            sprint={activeSprintQuery.data?.sprint}
            dataSource={dataSource}
            statusOptions={(workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses}
            estimationType={workspaceQuery.data?.estimationType}
            endSprint={() => toggleEndSprintModal(true)}
          />
        </div>
      )}

      {showZeroState && (
        <TasksZeroState
          onImportClick={props.onImportClick}
          viewGroup={activeView.group}
          entityType={activeView.entityType}
        />
      )}
      {showTasks && (
        <div
          // Was two hard-coded viewport formulas (sprint vs not) that had to track the
          // height of the chrome above the list; both were 11px short, and the scroll
          // container inside derived its own, third formula. The mismatch was clipped
          // away by `overflow-hidden`, eating the reserved bottom space. Fill by flex
          // instead so no level needs to know the chrome height. #2357
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
          ref={scrollRestorationContext?.containerRef}
        >
          {activeView.entityType === WorkspaceViewEntityType.task && (
            // Passes the flex fill through to the list so it can size itself off the
            // wrapper above rather than off the viewport. #2357
            <div className="flex min-h-0 flex-1 flex-col">
              {noResults && noResultsScreen}

              {!noResults && (() => {
                // Build tasksOrderMap from workspace TaskOrder - read directly from cache for freshest data
                const cachedWorkspace = queryClient.getQueryData<Workspace>(['workspace', actualSpaceId]);
                const taskOrder = cachedWorkspace?.TaskOrder[0];
                const tasksOrderMap = new Map<number, number>(
                  taskOrder?.taskIds?.map((id: number, index: number) => [id, index])
                );
                const statusOrder = sortStatusOptions(
                  (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses
                ).map((status) => status.id);

                // Sort tasks in each group
                const sortedTasksPerGroup = tasksPerGroup.map((group) => ({
                  ...group,
                  tasks: sortGroupTasks(
                    group.tasks,
                    tasksOrderMap,
                    activeView.sortBy?.column,
                    activeView.sortBy?.direction,
                    activeSprintId ? String(activeSprintId) : undefined,
                    planningSessionId ? String(planningSessionId) : undefined,
                    workspaceQuery.data?.estimationType,
                    statusOrder,
                    usersMap
                  ),
                }));

                // Sort unassigned tasks as well
                const sortedUnassignedTasks = sortGroupTasks(
                  unassignedTasks,
                  tasksOrderMap,
                  activeView.sortBy?.column,
                  activeView.sortBy?.direction,
                  activeSprintId ? String(activeSprintId) : undefined,
                  planningSessionId ? String(planningSessionId) : undefined,
                  workspaceQuery.data?.estimationType,
                  statusOrder,
                  usersMap
                );

                return (
                  <VirtualizedTaskList
                    tasksPerGroup={sortedTasksPerGroup}
                    unassignedTasks={sortedUnassignedTasks}
                    showUnassignedGroup={showNotAssignedGroup()}
                    groupBy={activeView.groupBy ?? Column.epic}
                    usersWithAccessToSpace={usersWithAccessToSpace}
                    showMultiselect={!planning}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    customRowSelection={planning ? planning.customRowSelection : undefined}
                    taskMenuProps={virtualizedTaskMenuProps}
                    onSwitchToManualSort={handleSwitchToManualSort}
                    dataSource={dataSource}
                    setDataSource={setDataSource}
                    handleEditEstimationSettings={handleEditEstimationSettings}
                    onSprintToPlanningConfirm={handleSprintToPlanningConfirm}
                    viewId={activeView.id}
                    searchValue={searchValue}
                    filtersSignature={JSON.stringify(selectedFilters)}
                  />
                );
              })()}
            </div>
          )}
          {isEpicView && epicNoResults && noResultsScreen}
          {isEpicView && !epicNoResults && (
            // Epics are a flat, ungrouped list: `groupBy: 'none'` suppresses the group
            // header and the inline add-task row (epics are created via "Create New").
            // VirtualizedTaskList brings its own scroll container and bottom batch-space,
            // so the bespoke wrapper added for #2518/#2542 is no longer needed. (#2544)
            <VirtualizedTaskList
              tasksPerGroup={[]}
              unassignedTasks={sortedEpics}
              showUnassignedGroup={true}
              groupBy={'none'}
              usersWithAccessToSpace={usersWithAccessToSpace}
              showMultiselect={!planning}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              customRowSelection={planning ? planning.customRowSelection : undefined}
              taskMenuProps={virtualizedTaskMenuProps}
              onSwitchToManualSort={handleSwitchToManualSort}
              dataSource={dataSource}
              setDataSource={setDataSource}
              handleEditEstimationSettings={handleEditEstimationSettings}
              viewId={activeView.id}
              searchValue={searchValue}
              filtersSignature={JSON.stringify(selectedFilters)}
            />
          )}
        </div>
      )}
      {selectedItems.length > 0 && (
        <ActionPanel
          selectedItems={selectedItems}
          onDeleteSelected={handleDeleteSelected}
          setSelectedItems={setSelectedItems}
          framework={workspaceQuery.data?.framework || WorkspaceFramework.scrum}
          epics={epics}
          usersWithAccessToSpace={usersWithAccessToSpace}
          onBatchEdit={(fieldType, field, action) => onBatchEdit(fieldType, field, selectedItems, action)}
        />
      )}

      {isFiltersModalOpen && (
        <TaskFiltersModal
          isOpen={isFiltersModalOpen}
          onClose={() => setIsFiltersModalOpen(false)}
          onSave={(selectedFilters) => setFilters(selectedFilters)}
          filters={filters}
          viewSelectedFilters={activeView.filters || []}
          isViewSelectedFilters={isViewSelectedFilters}
          resetToSavedFilters={handleResetToSaved}
          saveFiltersForAll={() => handleSaveForAll(filters)}
        />
      )}

      <DeleteTasksModal
        open={openDeleteModal}
        onOk={handleConfirmDeleteTasks}
        confirmLoading={confirmLoadingDeleteTasks}
        onCancel={handleCancelDelete}
        tasks={dataSource
          .flatMap((task) => [task, ...(task.subtasks || [])])
          .filter((task) => deleteTaskIds.includes(task.id))
          .sort((a, b) => {
            const aIndex = deleteTaskIds.indexOf(a.id);
            const bIndex = deleteTaskIds.indexOf(b.id);
            return aIndex - bIndex;
          })}
      />

      {framework === WorkspaceFramework.scrum && props.activeView.group === WorkspaceViewGroup.sprint && (
        <EndSprintModal
          open={endSprintModalVisible}
          onOk={handleEndSprint}
          onCancel={() => toggleEndSprintModal(false)}
          unfinishedTasksLength={unfinishedTasks.length}
        />
      )}

      <MoveSprintTaskToPlanningModal
        open={showSprintToPlanningModal}
        onCancel={() => {
          setShowSprintToPlanningModal(false);
          setRowActionTask(null);
        }}
        onOk={() => {
          if (rowActionTask) {
            moveTaskToPlanning(rowActionTask);
          }
          setRowActionTask(null);
          setShowSprintToPlanningModal(false);
        }}
        confirmLoading={false}
      />
    </div>
  );
};

export default TaskManagement;
